import { useDeliveryApi } from 'infrastructure/api';
import { cloneDeep } from 'lodash';
import {
  CreateBillingScheduleRequest,
  UpdateBillingScheduleRequest,
  BillingScheduleSearchRequest,
  BillingScheduleSearchResponse,
  BillingScheduleSensitiveSearchResponse,
  CreateBillingRequestPayload,
  MigrateBillingRequestPayload,
  UpdateBillingRequestPayload,
  ApproveBillingRequestPayload,
  UpdateInvoiceDatesPayload,
  BillingRequestResponse,
  BillingRequestSearchResultItem,
  BillingScheduleItemsSearchRequest,
  BillingScheduleItemResult,
  BillingScheduleResponse,
  UpdateMigratedBillingRequestPayload,
} from './billingTypes';
import {
  billingRequestRejectValidationSchema,
  InitiateBillingValidationSchema,
  MigrateBillingValidationSchema,
} from './initiateBillingValidation';
import { ValidationSchema } from 'framework/ui/dashboard';

export * from './billingTypes';

interface BillingService {
  createBillingSchedule: (request: CreateBillingScheduleRequest) => Promise<BillingScheduleResponse>;
  updateBillingSchedule: (
    billingScheduleId: string,
    request: UpdateBillingScheduleRequest,
  ) => Promise<BillingScheduleResponse>;
  billingScheduleSearch: (request: BillingScheduleSearchRequest) => Promise<BillingScheduleSearchResponse>;
  billingScheduleSensitiveSearch: (
    request: BillingScheduleSearchRequest,
  ) => Promise<BillingScheduleSensitiveSearchResponse>;
  createBillingRequest: (
    billingScheduleId: string,
    request: CreateBillingRequestPayload,
  ) => Promise<BillingRequestResponse>;
  migrateBillingRequest: (
    billingScheduleId: string,
    request: MigrateBillingRequestPayload,
  ) => Promise<BillingRequestResponse>;
  updateBillingRequest: (
    billingScheduleId: string,
    billingRequestId: string,
    request: UpdateBillingRequestPayload,
  ) => Promise<BillingRequestResponse>;
  updateMigratedBillingRequest: (
    billingScheduleId: string,
    billingRequestId: string,
    request: UpdateMigratedBillingRequestPayload,
  ) => Promise<BillingRequestResponse>;
  approveBillingRequest: (
    billingScheduleId: string,
    billingRequestId: string,
    request: ApproveBillingRequestPayload,
  ) => Promise<BillingRequestResponse>;
  rejectBillingRequest: (
    billingScheduleId: string,
    billingRequestId: string,
    rejectionReason: string,
  ) => Promise<BillingRequestResponse>;
  getBillingRequest: (billingScheduleId: string, billingRequestId: string) => Promise<BillingRequestSearchResultItem>;
  searchBillingScheduleItems: (
    scheduleId: string,
    request: BillingScheduleItemsSearchRequest,
  ) => Promise<BillingScheduleItemResult[]>;
  updateInvoiceDates: (billingScheduleId: string, request: UpdateInvoiceDatesPayload) => Promise<void>;
  getInitiateBillingValidationSchema: () => Promise<ValidationSchema>;
  getMigrateBillingValidationSchema: () => Promise<ValidationSchema>;
  getBillingRequestRejectValidationSchema: () => Promise<ValidationSchema>;
}

const useBillingService = (): BillingService => {
  const { sendPostRequest, sendGetRequest, sendPutRequest, sendPatchRequest } = useDeliveryApi();

  const createBillingSchedule = async (request: CreateBillingScheduleRequest): Promise<BillingScheduleResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPostRequest('/billing-schedule', requestData);
    return response.json();
  };

  const updateBillingSchedule = async (
    billingScheduleId: string,
    request: UpdateBillingScheduleRequest,
  ): Promise<BillingScheduleResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPatchRequest(`/billing-schedule/${billingScheduleId}`, requestData);
    return response.json();
  };

  const billingScheduleSearch = async (request: BillingScheduleSearchRequest): Promise<any> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/billing-schedule/search`, searchParams);
    return response.json();
  };

  const billingScheduleSensitiveSearch = async (
    request: BillingScheduleSearchRequest,
  ): Promise<BillingScheduleSensitiveSearchResponse> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/billing-schedule/sensitive/search`, searchParams);
    return response.json();
  };

  const createBillingRequest = async (
    billingScheduleId: string,
    request: CreateBillingRequestPayload,
  ): Promise<BillingRequestResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPostRequest(`/billing-schedule/${billingScheduleId}/billing-requests`, requestData);
    return response.json();
  };

  const migrateBillingRequest = async (
    billingScheduleId: string,
    request: MigrateBillingRequestPayload,
  ): Promise<BillingRequestResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPostRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/migrate`,
      requestData,
    );
    return response.json();
  };

  const updateBillingRequest = async (
    billingScheduleId: string,
    billingRequestId: string,
    request: UpdateBillingRequestPayload,
  ): Promise<BillingRequestResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPutRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/${billingRequestId}`,
      requestData,
    );
    return response.json();
  };

  const approveBillingRequest = async (
    billingScheduleId: string,
    billingRequestId: string,
    request: ApproveBillingRequestPayload,
  ): Promise<BillingRequestResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPutRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/${billingRequestId}/approve`,
      requestData,
    );
    return response.json();
  };

  const rejectBillingRequest = async (
    billingScheduleId: string,
    billingRequestId: string,
    rejectionReason: string,
  ): Promise<BillingRequestResponse> => {
    const response = await sendPutRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/${billingRequestId}/reject`,
      { rejectionReason },
    );
    return response.json();
  };

  const getBillingRequest = async (
    billingScheduleId: string,
    billingRequestId: string,
  ): Promise<BillingRequestSearchResultItem> => {
    const response = await sendGetRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/${billingRequestId}`,
    );
    return response.json();
  };

  const searchBillingScheduleItems = async (
    scheduleId: string,
    request: BillingScheduleItemsSearchRequest,
  ): Promise<BillingScheduleItemResult[]> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/billing-schedule/${scheduleId}/items/search`, searchParams);
    return response.json();
  };

  const updateMigratedBillingRequest = async (
    billingScheduleId: string,
    billingRequestId: string,
    request: UpdateMigratedBillingRequestPayload,
  ): Promise<BillingRequestResponse> => {
    const requestData = cloneDeep(request);
    const response = await sendPutRequest(
      `/billing-schedule/${billingScheduleId}/billing-requests/${billingRequestId}/migrate`,
      requestData,
    );
    return response.json();
  };

  const updateInvoiceDates = async (billingScheduleId: string, request: UpdateInvoiceDatesPayload): Promise<void> => {
    const requestData = cloneDeep(request);
    await sendPatchRequest(`/billing-schedule/${billingScheduleId}/items/invoice-dates`, requestData);
  };

  const getIniBillingValidationSchema = (): Promise<ValidationSchema> =>
    Promise.resolve(InitiateBillingValidationSchema as unknown as ValidationSchema);
  const getMigrateBillingValidationSchema = (): Promise<ValidationSchema> =>
    Promise.resolve(MigrateBillingValidationSchema as unknown as ValidationSchema);
  const getChangeRequestRejectValidationSchema = (): Promise<ValidationSchema> =>
    Promise.resolve(billingRequestRejectValidationSchema as unknown as ValidationSchema);

  return {
    createBillingSchedule,
    updateBillingSchedule,
    billingScheduleSearch,
    billingScheduleSensitiveSearch,
    createBillingRequest,
    migrateBillingRequest,
    updateBillingRequest,
    updateMigratedBillingRequest,
    approveBillingRequest,
    rejectBillingRequest,
    getBillingRequest,
    searchBillingScheduleItems,
    updateInvoiceDates,
    getInitiateBillingValidationSchema: getIniBillingValidationSchema,
    getMigrateBillingValidationSchema,
    getBillingRequestRejectValidationSchema: getChangeRequestRejectValidationSchema,
  };
};

export default useBillingService;
